import React, { useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight, CheckCircle, Image as ImageIcon } from 'lucide-react';

/**
 * Step-through review of every lens-colour variant of one frame (upc).
 *
 * A verdict applies to the whole group, so the reviewer has to actually look at
 * each variant before deciding. Progress is tracked by how far through the
 * carousel they have got - `onAllSeen` fires once the last variant is reached.
 */
function VariantCarousel({ variants, onAllSeen }) {
  const [index, setIndex] = useState(0);
  const [seen, setSeen] = useState(() => new Set([0]));

  const total = variants.length;
  const allSeen = seen.size >= total;

  useEffect(() => {
    if (allSeen) {
      onAllSeen?.();
    }
  }, [allSeen, onAllSeen]);

  const go = useCallback((next) => {
    if (next < 0 || next >= total) return;
    setIndex(next);
    setSeen((prev) => {
      const updated = new Set(prev);
      updated.add(next);
      return updated;
    });
  }, [total]);

  // Arrow keys mirror the buttons; the annotator isn't mounted yet in this
  // phase so there's nothing to conflict with.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'ArrowLeft') { e.preventDefault(); go(index - 1); }
      if (e.key === 'ArrowRight') { e.preventDefault(); go(index + 1); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [go, index]);

  const current = variants[index];

  return (
    <div className="flex-1 flex flex-col overflow-hidden bg-white">
      <div className="flex-1 flex items-center justify-center p-4 overflow-hidden bg-gray-50">
        <button
          onClick={() => go(index - 1)}
          disabled={index === 0}
          className="p-3 mr-2 rounded-full bg-white border border-gray-300 shadow-sm hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed flex-shrink-0"
          aria-label="Previous variant"
        >
          <ChevronLeft className="w-6 h-6 text-gray-700" />
        </button>

        <div className="flex-1 h-full flex flex-col items-center justify-center min-w-0">
          {current?.image_url ? (
            <img
              src={current.image_url}
              alt={current.lens_color || 'variant'}
              className="max-h-full max-w-full object-contain rounded-lg border border-gray-200 bg-white"
            />
          ) : (
            <div className="flex flex-col items-center gap-2 text-gray-400">
              <ImageIcon className="w-12 h-12" />
              <span className="text-sm">No image available</span>
            </div>
          )}
          <div className="mt-3 text-center">
            <div className="text-sm font-semibold text-gray-900">
              {current?.lens_color || 'Unknown lens colour'}
            </div>
            <div className="text-xs text-gray-500">
              Variant {index + 1} of {total}
            </div>
          </div>
        </div>

        <button
          onClick={() => go(index + 1)}
          disabled={index >= total - 1}
          className="p-3 ml-2 rounded-full bg-white border border-gray-300 shadow-sm hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed flex-shrink-0"
          aria-label="Next variant"
        >
          <ChevronRight className="w-6 h-6 text-gray-700" />
        </button>
      </div>

      {/* Filmstrip: jumping straight to a variant also counts as seeing it. */}
      <div className="border-t border-gray-200 bg-white px-4 py-3 flex-shrink-0">
        <div className="flex items-center gap-2 overflow-x-auto">
          {variants.map((v, i) => (
            <button
              key={v.node_id}
              onClick={() => go(i)}
              title={v.lens_color || `Variant ${i + 1}`}
              className={`relative flex-shrink-0 w-16 h-16 rounded border-2 overflow-hidden transition-all ${
                i === index ? 'border-indigo-600 ring-2 ring-indigo-200' : 'border-gray-300 hover:border-gray-400'
              }`}
            >
              {v.image_url ? (
                <img src={v.image_url} alt={v.lens_color || ''} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full bg-gray-100 flex items-center justify-center">
                  <ImageIcon className="w-5 h-5 text-gray-400" />
                </div>
              )}
              {seen.has(i) && (
                <div className="absolute top-0 right-0 bg-green-600 rounded-bl px-0.5">
                  <CheckCircle className="w-3 h-3 text-white" />
                </div>
              )}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

export default VariantCarousel;
